import { Button, createStyles, makeStyles, Theme } from "@material-ui/core";
import * as React from "react";
import { LetterGuessState } from "../../../Definitions/Definitions";
import * as ThemeConstants from "../../../Styles/Constants";
import { useWindowDimensions } from "../UseWindowDimensions";

export interface OverrideRowStyles {
    rowIndex: number;
    invalidColor?: string;
    positionColor?: string;
    matchColor?: string;
    invalidOpacity?: number;
    positionOpacity?: number;
    matchOpacity?: number;
    hideText?: boolean;
}

interface Props {
    rows: number;
    cols: number;
    words: string[];
    matches: LetterGuessState[][],
    animate?: boolean;
    invalidColor?: string;
    positionColor?: string;
    matchColor?: string;
    invalidOpacity?: number;
    positionOpacity?: number;
    matchOpacity?: number;
    size: "sm" | "md" | "lg";
    overrideRowStyles?: OverrideRowStyles[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
        },
        row: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        elem: {
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: ThemeConstants.PALETTE_WHITE
        },
    })
);


export const LetterGrid = (props: Props) => {
    const classes = useStyles();
    const { width, height } = useWindowDimensions();

    const small = width < 600;
    const {
        rows, cols, words, matches, size, animate,
        invalidColor, positionColor, matchColor,
        invalidOpacity, positionOpacity, matchOpacity,
        overrideRowStyles
    } = props;

    const getGapSize = () => {
        switch (size) {
            default: return "0px";
            case "sm": return small ? "0.075rem" : "0.15rem";
            case "md": return small ? "0.1rem" : "0.2rem";
            case "lg": return small ? "0.2rem" : `${0.4 * Math.min(1, height / 1000)}rem`;
        }
    }

    const getCellSize = () => {
        switch (size) {
            default: return 0;
            case "sm": return small ? 15 : 30;
            case "md": return small ? 20 : 40;
            case "lg": return (small ? 37 : 75 * Math.min(1, height / 1000)) / ((cols / 5) * (small ? 1.25 : 1));
        }
    }

    const getFontSize = () => {
        switch (size) {
            default: return 0;
            case "sm": return 9;
            case "md": return small ? 6 : 12;
            case "lg": return small ? 12 : 24;
        }
    }

    const renderGridRow = (rowIndex: number) => {
        let elems: JSX.Element[] = [];

        const cellSize = getCellSize();
        const fontSize = getFontSize();

        for (let i = 0; i < cols; i++) {
            let opacity = 0.2;
            if (rowIndex < words.length)
                opacity = 1.0;

            let finalInvalidColor = invalidColor ?? ThemeConstants.GAME_BOX_DEFAULT_COLOR;
            let finalMatchColor = matchColor ?? ThemeConstants.GAME_BOX_MATCH_COLOR;
            let finalPositionColor = positionColor ?? ThemeConstants.GAME_BOX_POSITION_COLOR;

            let finalInvalidOpacity = invalidOpacity ?? opacity;
            let finalMatchOpacity = matchOpacity ?? opacity;
            let finalPositionOpacity = positionOpacity ?? opacity;

            const overrideIndex = overrideRowStyles?.findIndex((e) => e.rowIndex == rowIndex) ?? -1;
            if (overrideRowStyles && overrideIndex != -1) {
                finalInvalidColor = overrideRowStyles[overrideIndex].invalidColor ?? finalInvalidColor;
                finalMatchColor = overrideRowStyles[overrideIndex].matchColor ?? finalMatchColor;
                finalPositionColor = overrideRowStyles[overrideIndex].positionColor ?? finalPositionColor;

                finalInvalidOpacity = overrideRowStyles[overrideIndex].invalidOpacity ?? finalInvalidOpacity;
                finalMatchOpacity = overrideRowStyles[overrideIndex].matchOpacity ?? finalMatchOpacity;
                finalPositionOpacity = overrideRowStyles[overrideIndex].positionOpacity ?? finalPositionOpacity;
            }

            const hideText = (overrideRowStyles && overrideIndex != -1 && overrideRowStyles[overrideIndex].hideText) ?? false;

            let bkgColor = finalInvalidColor;
            if (rowIndex < matches.length && rowIndex < words.length) {
                if (matches[rowIndex][i] == LetterGuessState.Match) {
                    bkgColor = finalMatchColor;
                    opacity = finalMatchOpacity;
                }
                else if (matches[rowIndex][i] == LetterGuessState.CorrectLetter) {
                    bkgColor = finalPositionColor;
                    opacity = finalPositionOpacity;
                }
                else
                    opacity = finalInvalidOpacity;
            }

            elems.push(
                <div
                    className={classes.elem}
                    key={i}
                    style={{
                        transition: animate || animate == undefined ? "background-color 100ms ease 0s, opacity 100ms ease 0s" : "",
                        backgroundColor: bkgColor,
                        opacity,
                        minWidth: cellSize,
                        minHeight: cellSize,
                        fontSize,
                        border: `${width < 600 ? 1 : 2}px solid ${ThemeConstants.LIGHT_GRAY}`,
                    }}
                >
                    {(!hideText && rowIndex < words.length && i < words[rowIndex].length) ? words[rowIndex][i].toUpperCase() : ""}
                </div>
            );
        }

        return elems;
    }

    const renderGrid = () => {
        let elems: JSX.Element[] = [];

        const gapSize = getGapSize();

        for (let i = 0; i < rows; i++) {
            elems.push(
                <div className={classes.row} key={i} style={{ gap: gapSize }}>
                    {renderGridRow(i)}
                </div>
            );
        }

        return elems;
    }

    return (
        <div className={classes.root} style={{ gap: getGapSize() }}>
            {renderGrid()}
        </div>
    );
}